import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CaretLeft, CaretRight, MapPin, Trash, Check, ArrowsClockwise, Lightning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { TimeGrid } from "@/components/schedule/TimeGrid";
import { TaskPanel } from "@/components/schedule/TaskPanel";
import {
  weekDays, startOfWeek, addDays, dateKey, toWall, parseWall, durationMin, fmtRange,
  WORK_MODES, taskColor,
} from "@/components/schedule/utils";
import { toast } from "sonner";

const conflictToast = (e) => {
  if (e?.response?.status === 409) toast.error("That slot overlaps a meeting or block");
  else toast.error("Something went wrong");
};

export default function Schedule() {
  const qc = useQueryClient();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [dragItem, setDragItem] = useState(null);
  const [addSlot, setAddSlot] = useState(null);
  const [blockDetail, setBlockDetail] = useState(null);
  const [wmOpen, setWmOpen] = useState(false);

  const { data: events = [] } = useQuery({ queryKey: ["calendar"], queryFn: api.calendar });
  const { data: blocks = [] } = useQuery({ queryKey: ["blocks"], queryFn: api.blocks });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: api.tasks });
  const { data: modes = [] } = useQuery({ queryKey: ["workModes"], queryFn: api.workModes });

  const days = useMemo(() => (view === "week" ? weekDays(anchor) : [anchor]), [view, anchor]);
  const weekStartKey = dateKey(startOfWeek(anchor));
  const slotH = view === "week" ? 26 : 44;

  const invBlocks = () => { qc.invalidateQueries({ queryKey: ["blocks"] }); qc.invalidateQueries({ queryKey: ["tasks"] }); };

  const createBlock = useMutation({ mutationFn: api.createBlock, onSuccess: invBlocks, onError: conflictToast });
  const moveBlock = useMutation({ mutationFn: ({ id, start, end }) => api.moveBlock(id, { start, end }), onSuccess: invBlocks, onError: conflictToast });
  const deleteBlock = useMutation({ mutationFn: api.deleteBlock, onSuccess: () => { invBlocks(); setBlockDetail(null); toast("Block removed"); } });
  const toggleBlock = useMutation({ mutationFn: api.toggleBlock, onSuccess: () => { invBlocks(); setBlockDetail(null); } });

  const createTask = useMutation({ mutationFn: api.createTask, onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Task added"); } });
  const deleteTask = useMutation({ mutationFn: api.deleteTask, onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); invBlocks(); } });
  const autoSchedule = useMutation({
    mutationFn: (id) => api.autoSchedule(id, weekStartKey),
    onSuccess: (d) => { invBlocks(); toast.success(d.created > 0 ? `Placed ${d.created} block${d.created > 1 ? "s" : ""} this week` : "No free slots found — try another week"); },
    onError: () => toast.error("Could not auto-schedule"),
  });

  const createWM = useMutation({ mutationFn: api.createWorkMode, onSuccess: () => qc.invalidateQueries({ queryKey: ["workModes"] }) });
  const deleteWM = useMutation({ mutationFn: api.deleteWorkMode, onSuccess: () => qc.invalidateQueries({ queryKey: ["workModes"] }) });

  const handleDropTask = (taskId, start) => {
    const t = tasks.find((x) => x.id === taskId);
    const size = t?.block_size || 30;
    const end = new Date(start.getTime() + size * 60000);
    createBlock.mutate({ task_id: taskId, start: toWall(start), end: toWall(end) });
  };
  const handleMoveBlock = (blockId, start) => {
    const b = blocks.find((x) => x.id === blockId);
    if (!b) return;
    const dur = durationMin(b.start, b.end);
    const end = new Date(start.getTime() + dur * 60000);
    moveBlock.mutate({ id: blockId, start: toWall(start), end: toWall(end) });
  };

  const title = view === "week"
    ? `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="px-6 sm:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-1">Time-blocking planner</div>
          <h1 className="font-heading text-3xl font-light tracking-tight text-slate-900">Schedule</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-xl p-1">
            {["day", "week"].map((v) => (
              <button
                key={v}
                data-testid={`view-${v}`}
                onClick={() => setView(v)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${view === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              >{v}</button>
            ))}
          </div>
          <Button data-testid="work-location-btn" onClick={() => setWmOpen(true)} variant="outline" className="rounded-xl gap-2">
            <MapPin weight="bold" size={16} /> Work location
          </Button>
        </div>
      </div>

      {/* nav + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button data-testid="prev-btn" onClick={() => setAnchor((a) => addDays(a, view === "week" ? -7 : -1))} variant="outline" size="icon" className="rounded-lg h-9 w-9"><CaretLeft size={16} /></Button>
          <Button data-testid="today-btn" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); }} variant="outline" className="rounded-lg h-9">Today</Button>
          <Button data-testid="next-btn" onClick={() => setAnchor((a) => addDays(a, view === "week" ? 7 : 1))} variant="outline" size="icon" className="rounded-lg h-9 w-9"><CaretRight size={16} /></Button>
          <span className="ml-2 font-heading font-medium text-slate-700">{title}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-slate-300" /> Meeting</span>
          {Object.entries(WORK_MODES).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded ${v.band}`} /> {v.label}</span>
          ))}
        </div>
      </div>

      <div className="flex gap-6 items-start">
        <div className="flex-1 min-w-0">
          <TimeGrid
            days={days}
            slotH={slotH}
            events={events}
            blocks={blocks}
            modes={modes}
            dragItem={dragItem}
            setDragItem={setDragItem}
            onSlotClick={(d) => setAddSlot(d)}
            onDropTask={handleDropTask}
            onMoveBlock={handleMoveBlock}
            onBlockClick={(b) => setBlockDetail(b)}
          />
        </div>
        <TaskPanel
          tasks={tasks}
          setDragItem={setDragItem}
          onCreate={(p) => createTask.mutate(p)}
          onDelete={(id) => deleteTask.mutate(id)}
          onAutoSchedule={(id) => autoSchedule.mutate(id)}
          autoSchedulingId={autoSchedule.isPending ? autoSchedule.variables : null}
          suggestFn={(title, desc) => api.suggestBlocks(title, desc)}
        />
      </div>

      <AddBlockDialog start={addSlot} tasks={tasks} onClose={() => setAddSlot(null)} onCreate={(payload) => { createBlock.mutate(payload); setAddSlot(null); }} />
      <BlockDetailDialog block={blockDetail} onClose={() => setBlockDetail(null)} onToggle={(id) => toggleBlock.mutate(id)} onDelete={(id) => deleteBlock.mutate(id)} />
      <WorkModeDialog open={wmOpen} onClose={() => setWmOpen(false)} anchor={anchor} modes={modes} onCreate={(p) => createWM.mutate(p)} onDelete={(id) => deleteWM.mutate(id)} />
    </div>
  );
}

function AddBlockDialog({ start, tasks, onClose, onCreate }) {
  const [taskId, setTaskId] = useState(null);
  const [label, setLabel] = useState("");
  const [dur, setDur] = useState(30);
  if (!start) return null;
  const startD = start;
  const end = new Date(startD.getTime() + dur * 60000);

  const submit = () => {
    const payload = { start: toWall(startD), end: toWall(end) };
    if (taskId) payload.task_id = taskId;
    else payload.title = label || "Focus block";
    onCreate(payload);
    setTaskId(null); setLabel(""); setDur(30);
  };

  return (
    <Dialog open={!!start} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="add-block-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add a block</DialogTitle>
          <DialogDescription>
            {startD.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })} · {startD.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} – {end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Duration</div>
            <div className="flex gap-2">
              {[30, 60, 90, 120].map((d) => (
                <button key={d} data-testid={`dur-${d}`} onClick={() => setDur(d)} className={`px-3 py-1.5 rounded-lg text-sm border ${dur === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{d}m</button>
              ))}
            </div>
          </div>

          {tasks.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Link to a task</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto aria-scroll">
                {tasks.map((t) => {
                  const c = taskColor(t.color);
                  return (
                    <button key={t.id} data-testid={`pick-task-${t.id}`} onClick={() => { setTaskId((id) => (id === t.id ? null : t.id)); setLabel(""); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-colors ${taskId === t.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <span className={`h-2.5 w-2.5 rounded-full ${c.solid}`} />
                      <span className="truncate flex-1">{t.title}</span>
                      {taskId === t.id && <Check weight="bold" size={14} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!taskId && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Or a quick label</div>
              <Input data-testid="block-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Deep work, Lunch, Emails" className="rounded-xl" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="confirm-add-block" onClick={submit} className="rounded-xl bg-slate-900 hover:bg-slate-800">Add block</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlockDetailDialog({ block, onClose, onToggle, onDelete }) {
  if (!block) return null;
  const c = taskColor(block.color);
  return (
    <Dialog open={!!block} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="block-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${c.solid}`} /> {block.title}
          </DialogTitle>
          <DialogDescription>{fmtRange(block.start, block.end)} · {parseWall(block.start).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button data-testid="toggle-block-btn" onClick={() => onToggle(block.id)} variant="outline" className={`flex-1 rounded-xl gap-2 ${block.done ? "" : "border-green-700 text-green-800 hover:bg-green-50"}`}>
            <Check weight="bold" size={16} /> {block.done ? "Mark not done" : "Mark done"}
          </Button>
          <Button data-testid="delete-block-btn" onClick={() => onDelete(block.id)} variant="ghost" className="rounded-xl text-rose-700 hover:bg-rose-50 gap-2">
            <Trash size={16} /> Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WorkModeDialog({ open, onClose, anchor, modes, onCreate, onDelete }) {
  const [mode, setMode] = useState("office");
  const [day, setDay] = useState(dateKey(anchor));
  const [from, setFrom] = useState(9);
  const [to, setTo] = useState(13);

  const submit = () => {
    if (to <= from) { toast.error("End must be after start"); return; }
    const base = parseWall(day + "T00:00:00");
    const s = new Date(base); s.setHours(from, 0, 0, 0);
    const e = new Date(base); e.setHours(to, 0, 0, 0);
    onCreate({ mode, start: toWall(s), end: toWall(e) });
    toast.success("Work location added");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="work-mode-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Where are you working?</DialogTitle>
          <DialogDescription>Add a colored location band to a stretch of your day.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(WORK_MODES).map(([k, v]) => (
              <button key={k} data-testid={`mode-${k}`} onClick={() => setMode(k)}
                className={`py-2 rounded-xl text-sm font-medium border transition-colors ${mode === k ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className={`inline-block h-2.5 w-2.5 rounded-full mr-1.5 ${v.band}`} />{v.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-slate-400 font-medium">Day</label>
              <Input data-testid="wm-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="rounded-xl h-9 mt-1" />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium">From</label>
              <select data-testid="wm-from" value={from} onChange={(e) => setFrom(Number(e.target.value))} className="w-full h-9 mt-1 rounded-xl border border-slate-300 px-2 text-sm">
                {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{h}:00</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium">To</label>
              <select data-testid="wm-to" value={to} onChange={(e) => setTo(Number(e.target.value))} className="w-full h-9 mt-1 rounded-xl border border-slate-300 px-2 text-sm">
                {Array.from({ length: 25 }).map((_, h) => <option key={h} value={h}>{h}:00</option>)}
              </select>
            </div>
          </div>
          <Button data-testid="add-work-mode-btn" onClick={submit} className="w-full rounded-xl bg-slate-900 hover:bg-slate-800">Add band</Button>

          {modes.length > 0 && (
            <div className="pt-1 space-y-1.5 max-h-40 overflow-y-auto aria-scroll">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Current bands</div>
              {modes.map((m) => {
                const v = WORK_MODES[m.mode] || WORK_MODES.office;
                return (
                  <div key={m.id} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className={`h-2.5 w-2.5 rounded-full ${v.band}`} />
                    <span className="flex-1 truncate">{v.label} · {fmtRange(m.start, m.end)} · {parseWall(m.start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    <button data-testid={`delete-wm-${m.id}`} onClick={() => onDelete(m.id)} className="text-slate-300 hover:text-rose-600"><Trash size={14} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
